import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faVideo, faTimes, faUpload, faMagic, faSpinner } from '@fortawesome/free-solid-svg-icons';

interface MediaUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUseDescription: (description: string) => void;
  mediaType: 'image' | 'video';
  analyzeMedia: (file: File, userIdea: string, attributes: string, contextType: string) => Promise<any>;
  userIdea: string;
  attributes: string;
}

const MediaUploadModal: React.FC<MediaUploadModalProps> = ({
  isOpen, onClose, onUseDescription, mediaType, analyzeMedia, userIdea, attributes
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setDescription('');
    setError('');

    const allowedTypes = mediaType === 'image' 
      ? ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
      : ['video/mp4', 'video/mov', 'video/webm'];

    if (!allowedTypes.includes(file.type)) {
      setError(`Please upload a valid ${mediaType} file`);
      return;
    }

    const maxSize = mediaType === 'image' ? 10 * 1024 * 1024 : 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File too large. Maximum size is ${mediaType === 'image' ? '10MB' : '50MB'}`);
      return;
    }

    setUploadedFile(file);
  };

  const handleGenerateDescription = async () => {
    if (!uploadedFile) return;

    setIsAnalyzing(true);
    setError('');

    try {
      const result = await analyzeMedia(
        uploadedFile,
        mediaType,
        'scene analysis for prompt generation',
        'scene analysis'
      );

      console.log('🔍 Full API response:', result);
      console.log('🔍 Response keys:', Object.keys(result || {}));
      console.log('🔍 Success:', result?.success);
      console.log('🔍 SceneDescription:', result?.sceneDescription);
      console.log('🔍 Type of sceneDescription:', typeof result?.sceneDescription);
      console.log('🔍 Length of sceneDescription:', result?.sceneDescription?.length);

      if (result?.success) {
        if (result.sceneDescription) {
          setDescription(result.sceneDescription);
        } else {
          throw new Error('Scene description is missing from response');
        }
      } else {
        throw new Error(result?.message || 'Analysis failed');
      }
    } catch (error: any) {
      console.error('Error analyzing media:', error);
      setError(`Failed to analyze ${mediaType}: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleUseDescription = () => {
    if (description) {
      onUseDescription(description);
      handleClose();
    }
  };

  const handleClose = () => {
    setUploadedFile(null);
    setDescription('');
    setError('');
    setIsAnalyzing(false);
    onClose();
  };

  const removeFile = () => {
    setUploadedFile(null);
    setDescription('');
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div style={modalOverlayStyles}>
      <div style={modalContentStyles}>
        <div style={modalHeaderStyles}>
          <h3 style={modalTitleStyles}>
            <FontAwesomeIcon icon={mediaType === 'image' ? faImage : faVideo} style={{marginRight: '0.5rem'}} />
            Upload {mediaType === 'image' ? 'Image' : 'Video'}
          </h3>
          <button onClick={handleClose} style={closeButtonStyles}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {!uploadedFile ? (
          <div style={uploadSectionStyles}>
            <input
              type="file"
              id={`modal-${mediaType}-upload`}
              accept={mediaType === 'image' ? 'image/jpeg,image/jpg,image/png,image/webp' : 'video/mp4,video/mov,video/webm'}
              onChange={handleFileUpload}
              style={hiddenInputStyles}
            />
            <label htmlFor={`modal-${mediaType}-upload`} style={uploadLabelStyles}>
              <FontAwesomeIcon icon={mediaType === 'image' ? faImage : faVideo} style={uploadIconStyles} />
              <span style={uploadTextStyles}>Click to upload {mediaType}</span>
              <small style={uploadHintStyles}>
                {mediaType === 'image' 
                  ? 'JPG, PNG, or WebP • Up to 10MB'
                  : 'MP4, MOV, or WebM • Up to 50MB'
                }
              </small>
            </label>
          </div>
        ) : (
          <div style={uploadedFileStyles}>
            <div style={fileInfoStyles}>
              <FontAwesomeIcon icon={mediaType === 'image' ? faImage : faVideo} style={fileIconStyles} />
              <div style={fileDetailsStyles}>
                <span style={fileNameStyles}>{uploadedFile.name}</span>
                <small style={fileSizeStyles}>
                  {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                </small>
              </div>
            </div>
            <button onClick={removeFile} style={removeButtonStyles}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}

        {error && (
          <div style={errorStyles}>
            {error}
          </div>
        )}

        {uploadedFile && !description && (
          <button
            onClick={handleGenerateDescription}
            disabled={isAnalyzing}
            style={{...generateButtonStyles, ...(isAnalyzing ? generateButtonDisabledStyles : {})}}
          >
            {isAnalyzing ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin style={{marginRight: '0.5rem'}} />
                Analyzing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faMagic} style={{marginRight: '0.5rem'}} />
                Generate Description
              </>
            )}
          </button>
        )}

        {description && (
          <div style={descriptionSectionStyles}>
            <h4 style={descriptionTitleStyles}>Generated Description:</h4>
            <div style={descriptionContentStyles}>
              {description}
            </div>
            <button onClick={handleUseDescription} style={useDescriptionButtonStyles}>
              Use This Description
            </button>
          </div>
        )}

        <div style={modalFooterStyles}>
          <button onClick={handleClose} style={cancelButtonStyles}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Styles
const modalOverlayStyles: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '1rem'
};

const modalContentStyles: React.CSSProperties = {
  background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
  borderRadius: '12px',
  padding: '1.5rem',
  width: '100%',
  maxWidth: '500px',
  maxHeight: '90vh', // Reduced from 80vh to prevent overflow
  overflowY: 'auto',
  color: '#e0e0e0',
  border: '1px solid #3a3a3a'
};

const modalHeaderStyles: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1.5rem'
};

const modalTitleStyles: React.CSSProperties = {
  margin: 0,
  fontSize: '1.25rem',
  color: '#ffffff'
};

const closeButtonStyles: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#888',
  fontSize: '1.25rem',
  cursor: 'pointer',
  padding: '0.5rem',
  borderRadius: '4px',
  transition: 'color 0.2s ease'
};

const uploadSectionStyles: React.CSSProperties = {
  marginBottom: '1rem'
};

const hiddenInputStyles: React.CSSProperties = { display: 'none' };

const uploadLabelStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '2rem',
  border: '2px dashed #4a5568',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: '#1a1a1a'
};

const uploadIconStyles: React.CSSProperties = {
  fontSize: '2.5rem',
  color: '#3b82f6',
  marginBottom: '0.5rem'
};

const uploadTextStyles: React.CSSProperties = {
  fontSize: '1rem',
  fontWeight: '500',
  marginBottom: '0.25rem',
  color: '#e0e0e0'
};

const uploadHintStyles: React.CSSProperties = {
  color: '#888',
  fontSize: '0.875rem'
};

const uploadedFileStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '1rem',
  backgroundColor: '#2a2a2a',
  borderRadius: '8px',
  marginBottom: '1rem'
};

const fileInfoStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flex: 1
};

const fileIconStyles: React.CSSProperties = {
  fontSize: '1.5rem',
  color: '#3b82f6',
  marginRight: '0.75rem'
};

const fileDetailsStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column'
};

const fileNameStyles: React.CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: '500',
  color: '#e0e0e0'
};

const fileSizeStyles: React.CSSProperties = {
  fontSize: '0.75rem',
  color: '#888'
};

const removeButtonStyles: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#ef4444',
  cursor: 'pointer',
  padding: '0.5rem',
  borderRadius: '4px'
};

const errorStyles: React.CSSProperties = {
  padding: '0.75rem',
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: '6px',
  color: '#dc2626',
  fontSize: '0.875rem',
  marginBottom: '1rem'
};

const generateButtonStyles: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem 1rem',
  backgroundColor: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  marginBottom: '1rem'
};

const generateButtonDisabledStyles: React.CSSProperties = {
  backgroundColor: '#6b7280',
  cursor: 'not-allowed'
};

const descriptionSectionStyles: React.CSSProperties = {
  marginBottom: '1rem'
};

const descriptionTitleStyles: React.CSSProperties = {
  margin: '0 0 0.5rem 0',
  fontSize: '1rem',
  color: '#ffffff'
};

const descriptionContentStyles: React.CSSProperties = {
  padding: '1rem',
  backgroundColor: '#1a1a1a',
  borderRadius: '6px',
  fontSize: '0.875rem',
  lineHeight: '1.5',
  marginBottom: '1rem',
  border: '1px solid #3a3a3a',
  maxHeight: '150px', // Limit height
  overflowY: 'auto', // Enable scrolling
  wordWrap: 'break-word'
};

const useDescriptionButtonStyles: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem 1rem',
  backgroundColor: '#10b981',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease'
};

const modalFooterStyles: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '1rem'
};

const cancelButtonStyles: React.CSSProperties = {
  padding: '0.5rem 1rem',
  backgroundColor: 'transparent',
  color: '#888',
  border: '1px solid #4a5568',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.2s ease'
};

export default MediaUploadModal;
